/**
 * Objective Helpers
 * Utility functions for objective-aware campaign evaluation, KPI logic,
 * trend calculations, and budget pacing
 */

// ===== Campaign Objectives & KPI Mappings =====

/**
 * Campaign objective types and their configurations
 */
export const CAMPAIGN_OBJECTIVES = {
    CONVERSIONS: 'CONVERSIONS',
    SALES: 'SALES',
    LEAD_GENERATION: 'LEAD_GENERATION',
    TRAFFIC: 'TRAFFIC',
    CLICKS: 'CLICKS',
    AWARENESS: 'AWARENESS',
    REACH: 'REACH',
    BRAND_AWARENESS: 'BRAND_AWARENESS',
    ENGAGEMENT: 'ENGAGEMENT',
    PAGE_LIKES: 'PAGE_LIKES',
    VIDEO_VIEWS: 'VIDEO_VIEWS',
    APP_INSTALLS: 'APP_INSTALLS',
    STORE_TRAFFIC: 'STORE_TRAFFIC',
    MESSAGES: 'MESSAGES'
};

/**
 * Objective configuration with primary KPI, secondary KPIs, and thresholds
 */
export const OBJECTIVE_CONFIG = {
    [CAMPAIGN_OBJECTIVES.CONVERSIONS]: {
        primaryKPI: 'roas',
        secondaryKPIs: ['conversions', 'costPerConversion', 'conversionRate'],
        successThreshold: 2.0, // ROAS > 2x is success
        failureThreshold: 1.0, // ROAS < 1x is failure
        displayLabel: 'Conversions',
        icon: '💰',
        formatPrimary: (value) => `${value.toFixed(2)}x ROAS`
    },
    [CAMPAIGN_OBJECTIVES.SALES]: {
        primaryKPI: 'roas',
        secondaryKPIs: ['revenue', 'purchases', 'costPerPurchase'],
        successThreshold: 2.0,
        failureThreshold: 1.0,
        displayLabel: 'Sales',
        icon: '🛒',
        formatPrimary: (value) => `${value.toFixed(2)}x ROAS`
    },
    [CAMPAIGN_OBJECTIVES.LEAD_GENERATION]: {
        primaryKPI: 'costPerLead',
        secondaryKPIs: ['leads', 'leadRate', 'spend'],
        successThreshold: 50, // CPL below $50 is good (configurable)
        failureThreshold: 100, // CPL above $100 is bad
        displayLabel: 'Lead Generation',
        icon: '📋',
        formatPrimary: (value) => `$${value.toFixed(2)} CPL`,
        lowerIsBetter: true
    },
    [CAMPAIGN_OBJECTIVES.TRAFFIC]: {
        primaryKPI: 'cpc',
        secondaryKPIs: ['clicks', 'ctr', 'impressions'],
        successThreshold: 0.50, // CPC below $0.50 is good
        failureThreshold: 2.00, // CPC above $2 is bad
        displayLabel: 'Traffic',
        icon: '🔗',
        formatPrimary: (value) => `$${value.toFixed(2)} CPC`,
        lowerIsBetter: true
    },
    [CAMPAIGN_OBJECTIVES.CLICKS]: {
        primaryKPI: 'cpc',
        secondaryKPIs: ['clicks', 'ctr', 'impressions'],
        successThreshold: 0.50,
        failureThreshold: 2.00,
        displayLabel: 'Clicks',
        icon: '👆',
        formatPrimary: (value) => `$${value.toFixed(2)} CPC`,
        lowerIsBetter: true
    },
    [CAMPAIGN_OBJECTIVES.AWARENESS]: {
        primaryKPI: 'cpm',
        secondaryKPIs: ['impressions', 'reach', 'frequency'],
        successThreshold: 5.00, // CPM below $5 is good
        failureThreshold: 15.00, // CPM above $15 is bad
        displayLabel: 'Awareness',
        icon: '👁️',
        formatPrimary: (value) => `$${value.toFixed(2)} CPM`,
        lowerIsBetter: true
    },
    [CAMPAIGN_OBJECTIVES.REACH]: {
        primaryKPI: 'cpm',
        secondaryKPIs: ['reach', 'impressions', 'frequency'],
        successThreshold: 5.00,
        failureThreshold: 15.00,
        displayLabel: 'Reach',
        icon: '📢',
        formatPrimary: (value) => `$${value.toFixed(2)} CPM`,
        lowerIsBetter: true
    },
    [CAMPAIGN_OBJECTIVES.BRAND_AWARENESS]: {
        primaryKPI: 'cpm',
        secondaryKPIs: ['impressions', 'reach', 'estimatedRecall'],
        successThreshold: 5.00,
        failureThreshold: 15.00,
        displayLabel: 'Brand Awareness',
        icon: '🏷️',
        formatPrimary: (value) => `$${value.toFixed(2)} CPM`,
        lowerIsBetter: true
    },
    [CAMPAIGN_OBJECTIVES.ENGAGEMENT]: {
        primaryKPI: 'engagementRate',
        secondaryKPIs: ['engagements', 'costPerEngagement', 'likes', 'comments', 'shares'],
        successThreshold: 3.0, // Engagement rate above 3% is good
        failureThreshold: 1.0, // Below 1% is bad
        displayLabel: 'Engagement',
        icon: '💬',
        formatPrimary: (value) => `${value.toFixed(2)}% ER`
    },
    [CAMPAIGN_OBJECTIVES.PAGE_LIKES]: {
        primaryKPI: 'costPerPageLike',
        secondaryKPIs: ['pageLikes', 'engagementRate', 'cpm', 'impressions'],
        successThreshold: 0.50, // CPL below $0.50 is good
        failureThreshold: 2.00, // CPL above $2 is bad
        displayLabel: 'Page Likes',
        icon: '👍',
        formatPrimary: (value) => `$${value.toFixed(2)} CPL`,
        lowerIsBetter: true
    },
    [CAMPAIGN_OBJECTIVES.VIDEO_VIEWS]: {
        primaryKPI: 'costPerView',
        secondaryKPIs: ['videoViews', 'viewThroughRate', 'averageWatchTime'],
        successThreshold: 0.02, // CPV below $0.02 is good
        failureThreshold: 0.10, // Above $0.10 is bad
        displayLabel: 'Video Views',
        icon: '🎬',
        formatPrimary: (value) => `$${value.toFixed(3)} CPV`,
        lowerIsBetter: true
    },
    [CAMPAIGN_OBJECTIVES.APP_INSTALLS]: {
        primaryKPI: 'costPerInstall',
        secondaryKPIs: ['installs', 'installRate', 'clicks'],
        successThreshold: 2.00, // CPI below $2 is good
        failureThreshold: 10.00, // Above $10 is bad
        displayLabel: 'App Installs',
        icon: '📱',
        formatPrimary: (value) => `$${value.toFixed(2)} CPI`,
        lowerIsBetter: true
    },
    [CAMPAIGN_OBJECTIVES.STORE_TRAFFIC]: {
        primaryKPI: 'costPerVisit',
        secondaryKPIs: ['storeVisits', 'visitRate', 'impressions'],
        successThreshold: 5.00,
        failureThreshold: 20.00,
        displayLabel: 'Store Traffic',
        icon: '🏪',
        formatPrimary: (value) => `$${value.toFixed(2)} CPV`,
        lowerIsBetter: true
    },
    [CAMPAIGN_OBJECTIVES.MESSAGES]: {
        primaryKPI: 'costPerMessage',
        secondaryKPIs: ['messages', 'messageRate', 'clicks'],
        successThreshold: 1.00,
        failureThreshold: 5.00,
        displayLabel: 'Messages',
        icon: '💬',
        formatPrimary: (value) => `$${value.toFixed(2)} CPM`,
        lowerIsBetter: true
    }
};

/**
 * Get the normalized objective type from various platform formats
 * @param {string} objective - Raw objective string from platform
 * @returns {string} - Normalized objective type
 */
export const normalizeObjective = (objective) => {
    if (!objective) return CAMPAIGN_OBJECTIVES.CONVERSIONS;

    const normalized = objective.toUpperCase().replace(/[-_\s]/g, '_');

    // Map various platform-specific objectives to our standard types
    const objectiveMap = {
        'OUTCOME_SALES': CAMPAIGN_OBJECTIVES.SALES,
        'OUTCOME_TRAFFIC': CAMPAIGN_OBJECTIVES.TRAFFIC,
        'OUTCOME_ENGAGEMENT': CAMPAIGN_OBJECTIVES.ENGAGEMENT,
        'OUTCOME_LEADS': CAMPAIGN_OBJECTIVES.LEAD_GENERATION,
        'OUTCOME_APP_PROMOTION': CAMPAIGN_OBJECTIVES.APP_INSTALLS,
        'OUTCOME_AWARENESS': CAMPAIGN_OBJECTIVES.AWARENESS,
        'LINK_CLICKS': CAMPAIGN_OBJECTIVES.CLICKS,
        'WEBSITE_CONVERSIONS': CAMPAIGN_OBJECTIVES.CONVERSIONS,
        'PRODUCT_CATALOG_SALES': CAMPAIGN_OBJECTIVES.SALES,
        'LEAD_GEN': CAMPAIGN_OBJECTIVES.LEAD_GENERATION,
        'LEAD_GENERATION': CAMPAIGN_OBJECTIVES.LEAD_GENERATION,
        'VIDEO_VIEW': CAMPAIGN_OBJECTIVES.VIDEO_VIEWS,
        'POST_ENGAGEMENT': CAMPAIGN_OBJECTIVES.ENGAGEMENT,
        'PAGE_LIKES': CAMPAIGN_OBJECTIVES.PAGE_LIKES,
        'BRAND_AWARENESS': CAMPAIGN_OBJECTIVES.BRAND_AWARENESS,
        'REACH': CAMPAIGN_OBJECTIVES.REACH,
        'APP_INSTALLS': CAMPAIGN_OBJECTIVES.APP_INSTALLS,
        'CONVERSIONS': CAMPAIGN_OBJECTIVES.CONVERSIONS,
        'TRAFFIC': CAMPAIGN_OBJECTIVES.TRAFFIC,
        'AWARENESS': CAMPAIGN_OBJECTIVES.AWARENESS,
        'ENGAGEMENT': CAMPAIGN_OBJECTIVES.ENGAGEMENT,
        'SALES': CAMPAIGN_OBJECTIVES.SALES,
        'MESSAGES': CAMPAIGN_OBJECTIVES.MESSAGES
    };

    return objectiveMap[normalized] || CAMPAIGN_OBJECTIVES.CONVERSIONS;
};

/**
 * Get primary KPI for an objective
 * @param {string} objective - Campaign objective
 * @returns {string} - Primary KPI name
 */
export const getPrimaryKPI = (objective) => {
    const normalizedObjective = normalizeObjective(objective);
    const config = OBJECTIVE_CONFIG[normalizedObjective];
    return config?.primaryKPI || 'roas';
};

/**
 * Get secondary KPIs for an objective
 * @param {string} objective - Campaign objective
 * @returns {string[]} - Array of secondary KPI names
 */
export const getSecondaryKPIs = (objective) => {
    const normalizedObjective = normalizeObjective(objective);
    const config = OBJECTIVE_CONFIG[normalizedObjective];
    return config?.secondaryKPIs || ['spend', 'impressions', 'clicks'];
};

/**
 * Get objective configuration
 * @param {string} objective - Campaign objective
 * @returns {Object} - Full objective configuration
 */
export const getObjectiveConfig = (objective) => {
    const normalizedObjective = normalizeObjective(objective);
    return OBJECTIVE_CONFIG[normalizedObjective] || OBJECTIVE_CONFIG[CAMPAIGN_OBJECTIVES.CONVERSIONS];
};

// ===== Campaign Health Evaluation =====

/**
 * Health status levels
 */
export const HEALTH_STATUS = {
    EXCELLENT: 'excellent',
    GOOD: 'good',
    WARNING: 'warning',
    CRITICAL: 'critical',
    UNKNOWN: 'unknown'
};

/**
 * Evaluate campaign health based on its objective
 * @param {Object} campaign - Campaign object with metrics
 * @returns {Object} - Health evaluation result
 */
export const evaluateCampaignHealth = (campaign) => {
    const objective = normalizeObjective(campaign.objective);
    const config = OBJECTIVE_CONFIG[objective];
    const metrics = campaign.currentMetrics || campaign.insights?.data?.[0] || {};

    const primaryKPI = config.primaryKPI;
    const value = extractMetricValue(metrics, primaryKPI);

    if (value === null || value === undefined) {
        return {
            status: HEALTH_STATUS.UNKNOWN,
            score: 0,
            message: 'Insufficient data to evaluate',
            primaryKPI,
            value: null
        };
    }

    const { successThreshold, failureThreshold, lowerIsBetter } = config;

    let status, score, message;

    if (lowerIsBetter) {
        // Lower is better (CPL, CPC, CPM, etc.)
        if (value <= successThreshold * 0.7) {
            status = HEALTH_STATUS.EXCELLENT;
            score = 100;
            message = 'Performing exceptionally well';
        } else if (value <= successThreshold) {
            status = HEALTH_STATUS.GOOD;
            score = 75;
            message = 'On target';
        } else if (value <= failureThreshold) {
            status = HEALTH_STATUS.WARNING;
            score = 50;
            message = 'Needs attention';
        } else {
            status = HEALTH_STATUS.CRITICAL;
            score = 25;
            message = 'Underperforming';
        }
    } else {
        // Higher is better (ROAS, engagement rate, etc.)
        if (value >= successThreshold * 1.5) {
            status = HEALTH_STATUS.EXCELLENT;
            score = 100;
            message = 'Performing exceptionally well';
        } else if (value >= successThreshold) {
            status = HEALTH_STATUS.GOOD;
            score = 75;
            message = 'On target';
        } else if (value >= failureThreshold) {
            status = HEALTH_STATUS.WARNING;
            score = 50;
            message = 'Needs attention';
        } else {
            status = HEALTH_STATUS.CRITICAL;
            score = 25;
            message = 'Underperforming';
        }
    }

    return {
        status,
        score,
        message,
        primaryKPI,
        value,
        formattedValue: config.formatPrimary(value),
        objective: config.displayLabel
    };
};

/**
 * Helper to calculate total engagements from various engagement metrics
 * @param {Object} metrics - Metrics object
 * @returns {number} - Total engagements
 */
const getTotalEngagements = (metrics) => {
    return (parseFloat(metrics.postEngagements) || 0) +
           (parseFloat(metrics.pageLikes) || 0) +
           (parseFloat(metrics.comments) || 0) +
           (parseFloat(metrics.shares) || 0) +
           (parseFloat(metrics.linkClicks) || parseFloat(metrics.link_clicks) || 0) +
           (parseFloat(metrics.reactions) || 0);
};

/**
 * Extract metric value from various data structures
 * @param {Object} metrics - Metrics object
 * @param {string} kpi - KPI name
 * @returns {number|null} - Metric value
 */
export const extractMetricValue = (metrics, kpi) => {
    if (!metrics) return null;

    // Direct property access
    if (metrics[kpi] !== undefined && metrics[kpi] !== null) {
        const val = parseFloat(metrics[kpi]);
        if (!isNaN(val)) return val;
    }

    // Common alternative names
    const aliases = {
        roas: ['roas', 'return_on_ad_spend', 'returnOnAdSpend'],
        costPerLead: ['cost_per_lead', 'costPerLead', 'cpl', 'cost_per_result'],
        cpc: ['cpc', 'cost_per_click', 'costPerClick'],
        cpm: ['cpm', 'cost_per_mille', 'costPerMille', 'cost_per_1000_impressions'],
        engagementRate: ['engagement_rate', 'engagementRate', 'engagement_rate_ranking'],
        costPerView: ['cost_per_thruplay', 'cpv', 'costPerView', 'cost_per_video_view'],
        costPerInstall: ['cost_per_app_install', 'cpi', 'costPerInstall'],
        costPerPageLike: ['cost_per_page_like', 'costPerPageLike', 'cost_per_like'],
        costPerEngagement: ['cost_per_engagement', 'costPerEngagement', 'cpe'],
        costPerMessage: ['cost_per_message', 'costPerMessage'],
        costPerVisit: ['cost_per_visit', 'costPerVisit', 'cost_per_store_visit'],
        leads: ['leads', 'lead', 'results'],
        clicks: ['clicks', 'link_clicks', 'outbound_clicks'],
        impressions: ['impressions', 'imps'],
        conversions: ['conversions', 'purchases', 'results'],
        spend: ['spend', 'amount_spent', 'cost'],
        revenue: ['revenue', 'purchase_roas', 'conversion_value'],
        pageLikes: ['pageLikes', 'page_likes', 'likes'],
        videoViews: ['videoViews', 'video_views', 'thruplay', 'video_thruplay'],
        appInstalls: ['appInstalls', 'app_installs', 'installs', 'mobile_app_installs'],
        messages: ['messages', 'messaging_conversations', 'conversations_started'],
        storeVisits: ['storeVisits', 'store_visits'],
        engagements: ['engagements', 'post_engagements', 'postEngagements']
    };

    const aliasGroup = aliases[kpi];
    if (aliasGroup) {
        for (const alias of aliasGroup) {
            if (metrics[alias] !== undefined && metrics[alias] !== null) {
                const val = parseFloat(metrics[alias]);
                if (!isNaN(val)) return val;
            }
        }
    }

    // Get common values for calculations
    const spend = parseFloat(metrics.spend) || parseFloat(metrics.amount_spent) || 0;
    const impressions = parseFloat(metrics.impressions) || 0;
    const clicks = parseFloat(metrics.clicks) || parseFloat(metrics.link_clicks) || 0;

    // Calculate derived metrics with comprehensive fallbacks
    switch (kpi) {
        // === Revenue & ROAS ===
        case 'roas': {
            const revenue = parseFloat(metrics.revenue) || parseFloat(metrics.conversion_value) || 0;
            return spend > 0 ? revenue / spend : null;
        }

        // === Click Metrics ===
        case 'cpc':
            return clicks > 0 ? spend / clicks : null;

        case 'ctr':
            return impressions > 0 ? (clicks / impressions) * 100 : null;

        // === Impression Metrics ===
        case 'cpm':
            return impressions > 0 ? (spend / impressions) * 1000 : null;

        // === Lead Metrics ===
        case 'costPerLead': {
            const leads = parseFloat(metrics.leads) || parseFloat(metrics.lead) || 0;
            return leads > 0 ? spend / leads : null;
        }

        case 'leadRate': {
            const leads = parseFloat(metrics.leads) || parseFloat(metrics.lead) || 0;
            return clicks > 0 ? (leads / clicks) * 100 : null;
        }

        // === Engagement Metrics ===
        case 'engagementRate': {
            // First check if we have a pre-calculated value
            if (metrics.engagementRate) return parseFloat(metrics.engagementRate);
            // Calculate from engagements
            const engagements = getTotalEngagements(metrics);
            return impressions > 0 ? (engagements / impressions) * 100 : null;
        }

        case 'costPerEngagement': {
            if (metrics.costPerEngagement) return parseFloat(metrics.costPerEngagement);
            const engagements = getTotalEngagements(metrics);
            return engagements > 0 ? spend / engagements : null;
        }

        case 'engagements': {
            if (metrics.engagements) return parseFloat(metrics.engagements);
            return getTotalEngagements(metrics) || null;
        }

        // === Page Like Metrics ===
        case 'costPerPageLike': {
            if (metrics.costPerPageLike) return parseFloat(metrics.costPerPageLike);
            const pageLikes = parseFloat(metrics.pageLikes) || parseFloat(metrics.page_likes) || 0;
            return pageLikes > 0 ? spend / pageLikes : null;
        }

        case 'pageLikes': {
            return parseFloat(metrics.pageLikes) || parseFloat(metrics.page_likes) || null;
        }

        // === Video Metrics ===
        case 'costPerView': {
            if (metrics.costPerView) return parseFloat(metrics.costPerView);
            const videoViews = parseFloat(metrics.videoViews) || parseFloat(metrics.video_views) ||
                              parseFloat(metrics.thruplay) || 0;
            return videoViews > 0 ? spend / videoViews : null;
        }

        case 'videoViews': {
            return parseFloat(metrics.videoViews) || parseFloat(metrics.video_views) ||
                   parseFloat(metrics.thruplay) || null;
        }

        case 'viewThroughRate': {
            const videoViews = parseFloat(metrics.videoViews) || parseFloat(metrics.video_views) || 0;
            return impressions > 0 ? (videoViews / impressions) * 100 : null;
        }

        // === App Install Metrics ===
        case 'costPerInstall': {
            if (metrics.costPerInstall) return parseFloat(metrics.costPerInstall);
            const installs = parseFloat(metrics.appInstalls) || parseFloat(metrics.installs) ||
                            parseFloat(metrics.mobile_app_installs) || 0;
            return installs > 0 ? spend / installs : null;
        }

        case 'appInstalls':
        case 'installs': {
            return parseFloat(metrics.appInstalls) || parseFloat(metrics.installs) ||
                   parseFloat(metrics.mobile_app_installs) || null;
        }

        case 'installRate': {
            const installs = parseFloat(metrics.appInstalls) || parseFloat(metrics.installs) || 0;
            return clicks > 0 ? (installs / clicks) * 100 : null;
        }

        // === Store Traffic Metrics ===
        case 'costPerVisit': {
            if (metrics.costPerVisit) return parseFloat(metrics.costPerVisit);
            const storeVisits = parseFloat(metrics.storeVisits) || parseFloat(metrics.store_visits) || 0;
            return storeVisits > 0 ? spend / storeVisits : null;
        }

        case 'storeVisits': {
            return parseFloat(metrics.storeVisits) || parseFloat(metrics.store_visits) || null;
        }

        case 'visitRate': {
            const storeVisits = parseFloat(metrics.storeVisits) || parseFloat(metrics.store_visits) || 0;
            return impressions > 0 ? (storeVisits / impressions) * 100 : null;
        }

        // === Message Metrics ===
        case 'costPerMessage': {
            if (metrics.costPerMessage) return parseFloat(metrics.costPerMessage);
            const messages = parseFloat(metrics.messages) || parseFloat(metrics.messaging_conversations) || 0;
            return messages > 0 ? spend / messages : null;
        }

        case 'messages': {
            return parseFloat(metrics.messages) || parseFloat(metrics.messaging_conversations) || null;
        }

        case 'messageRate': {
            const messages = parseFloat(metrics.messages) || parseFloat(metrics.messaging_conversations) || 0;
            return clicks > 0 ? (messages / clicks) * 100 : null;
        }

        // === Purchase/Conversion Metrics ===
        case 'costPerPurchase':
        case 'costPerConversion': {
            const conversions = parseFloat(metrics.conversions) || parseFloat(metrics.purchases) || 0;
            return conversions > 0 ? spend / conversions : null;
        }

        case 'conversionRate': {
            const conversions = parseFloat(metrics.conversions) || parseFloat(metrics.purchases) || 0;
            return clicks > 0 ? (conversions / clicks) * 100 : null;
        }

        default:
            return null;
    }
};

// ===== Campaign Metric Formatting =====

/**
 * Format the primary metric for display in campaign cards
 * @param {Object} campaign - Campaign object
 * @returns {string} - Formatted metric string
 */
export const formatCampaignMetric = (campaign) => {
    const objective = normalizeObjective(campaign.objective);
    const config = OBJECTIVE_CONFIG[objective];
    const metrics = campaign.currentMetrics || campaign.insights?.data?.[0] || {};

    const value = extractMetricValue(metrics, config.primaryKPI);

    if (value === null || value === undefined) {
        return 'No data';
    }

    return config.formatPrimary(value);
};

/**
 * Get display data for campaign card
 * @param {Object} campaign - Campaign object
 * @returns {Object} - Display data
 */
export const getCampaignDisplayData = (campaign) => {
    const objective = normalizeObjective(campaign.objective);
    const config = OBJECTIVE_CONFIG[objective];
    const metrics = campaign.currentMetrics || campaign.insights?.data?.[0] || {};
    const spend = extractMetricValue(metrics, 'spend') || 0;

    return {
        objectiveLabel: config.displayLabel,
        objectiveIcon: config.icon,
        primaryMetric: formatCampaignMetric(campaign),
        spend: formatCurrency(spend),
        ...evaluateCampaignHealth(campaign)
    };
};

// ===== Trend Calculations =====

/**
 * Calculate trend between current and previous period
 * @param {number} current - Current value
 * @param {number} previous - Previous period value
 * @returns {Object} - Trend data with percentage and direction
 */
export const calculateTrend = (current, previous) => {
    if (previous === 0 || previous === null || previous === undefined) {
        return {
            percentage: 0,
            direction: 'neutral',
            formatted: '--',
            color: 'var(--color-text-secondary)'
        };
    }

    const percentage = ((current - previous) / Math.abs(previous)) * 100;
    const direction = percentage > 0 ? 'up' : percentage < 0 ? 'down' : 'neutral';

    return {
        percentage: Math.abs(percentage),
        direction,
        formatted: `${direction === 'up' ? '↑' : direction === 'down' ? '↓' : ''}${Math.abs(percentage).toFixed(1)}%`,
        color: direction === 'up' ? 'var(--color-success)' : direction === 'down' ? 'var(--color-error)' : 'var(--color-text-secondary)'
    };
};

/**
 * Calculate trend with objective awareness (some metrics are "lower is better")
 * @param {number} current - Current value
 * @param {number} previous - Previous period value
 * @param {string} objective - Campaign objective
 * @param {string} kpi - KPI being measured
 * @returns {Object} - Trend data with objective-aware coloring
 */
export const calculateObjectiveAwareTrend = (current, previous, objective, kpi = null) => {
    const trend = calculateTrend(current, previous);

    if (trend.direction === 'neutral') return trend;

    const normalizedObjective = normalizeObjective(objective);
    const config = OBJECTIVE_CONFIG[normalizedObjective];
    const isLowerBetter = config?.lowerIsBetter || false;

    // For "lower is better" KPIs, invert the color logic
    // Going down is good, going up is bad
    if (isLowerBetter && (kpi === config?.primaryKPI || kpi === null)) {
        const isImproved = trend.direction === 'down';
        trend.color = isImproved ? 'var(--color-success)' : 'var(--color-error)';
        trend.isPositive = isImproved;
    } else {
        trend.isPositive = trend.direction === 'up';
    }

    return trend;
};

// ===== Budget Pacing =====

/**
 * Pacing status types
 */
export const PACING_STATUS = {
    ON_TRACK: 'on-track',
    UNDERPACING: 'underpacing',
    OVERPACING: 'overpacing',
    COMPLETED: 'completed',
    UNKNOWN: 'unknown'
};

/**
 * Calculate budget pacing for a campaign
 * @param {Object} campaign - Campaign object
 * @returns {Object} - Pacing data
 */
export const calculateBudgetPacing = (campaign) => {
    const {
        dailyBudget,
        lifetimeBudget,
        startDate,
        endDate,
        budget_remaining,
        currentMetrics
    } = campaign;

    const spent = extractMetricValue(currentMetrics, 'spend') ||
                  campaign.insights?.data?.[0]?.spend ||
                  campaign.spend || 0;

    const now = new Date();
    const start = startDate ? new Date(startDate) : null;
    const end = endDate ? new Date(endDate) : null;

    // If campaign has a lifetime budget and end date
    if (lifetimeBudget && start && end) {
        const totalDays = daysBetween(start, end);
        const elapsedDays = daysBetween(start, now);

        if (totalDays <= 0) {
            return {
                status: PACING_STATUS.UNKNOWN,
                percentage: 0,
                expectedPercentage: 0,
                message: 'Invalid date range'
            };
        }

        // If campaign ended
        if (now > end) {
            const spentPercentage = (spent / lifetimeBudget) * 100;
            return {
                status: PACING_STATUS.COMPLETED,
                percentage: Math.min(spentPercentage, 100),
                expectedPercentage: 100,
                spent,
                budget: lifetimeBudget,
                message: `Campaign ended - ${spentPercentage.toFixed(0)}% of budget used`
            };
        }

        const expectedPercentage = Math.min((elapsedDays / totalDays) * 100, 100);
        const actualPercentage = (spent / lifetimeBudget) * 100;
        const pacingRatio = actualPercentage / expectedPercentage;

        let status, message;
        if (pacingRatio >= 0.85 && pacingRatio <= 1.15) {
            status = PACING_STATUS.ON_TRACK;
            message = 'On track';
        } else if (pacingRatio < 0.85) {
            status = PACING_STATUS.UNDERPACING;
            message = `Underpacing (should be ${expectedPercentage.toFixed(0)}%)`;
        } else {
            status = PACING_STATUS.OVERPACING;
            message = `Overpacing (should be ${expectedPercentage.toFixed(0)}%)`;
        }

        return {
            status,
            percentage: actualPercentage,
            expectedPercentage,
            spent,
            budget: lifetimeBudget,
            remaining: lifetimeBudget - spent,
            daysRemaining: Math.max(0, daysBetween(now, end)),
            message
        };
    }

    // Daily budget tracking
    if (dailyBudget) {
        const todaySpent = currentMetrics?.todaySpend || spent;
        const percentage = (todaySpent / dailyBudget) * 100;

        // Check time of day for expected pacing
        const hoursElapsed = now.getHours() + (now.getMinutes() / 60);
        const expectedPercentage = (hoursElapsed / 24) * 100;
        const pacingRatio = percentage / expectedPercentage;

        let status, message;
        if (pacingRatio >= 0.8 && pacingRatio <= 1.2) {
            status = PACING_STATUS.ON_TRACK;
            message = 'On track for daily budget';
        } else if (pacingRatio < 0.8) {
            status = PACING_STATUS.UNDERPACING;
            message = 'Underpacing daily budget';
        } else {
            status = PACING_STATUS.OVERPACING;
            message = 'Overpacing daily budget';
        }

        return {
            status,
            percentage,
            expectedPercentage,
            spent: todaySpent,
            budget: dailyBudget,
            remaining: Math.max(0, dailyBudget - todaySpent),
            message,
            isDaily: true
        };
    }

    // If we have budget_remaining, calculate what we can
    if (budget_remaining !== undefined && lifetimeBudget) {
        const spent = lifetimeBudget - budget_remaining;
        const percentage = (spent / lifetimeBudget) * 100;

        return {
            status: PACING_STATUS.UNKNOWN,
            percentage,
            spent,
            budget: lifetimeBudget,
            remaining: budget_remaining,
            message: `${percentage.toFixed(0)}% spent`
        };
    }

    return {
        status: PACING_STATUS.UNKNOWN,
        percentage: 0,
        message: 'No budget data'
    };
};

// ===== Campaign End Date Helpers =====

/**
 * Get campaign end date status and display text
 * @param {Object} campaign - Campaign object
 * @returns {Object} - End date status
 */
export const getCampaignEndStatus = (campaign) => {
    const { endDate, end_time } = campaign;
    const endDateValue = endDate || end_time;

    if (!endDateValue) {
        return {
            hasEndDate: false,
            display: 'Ongoing',
            icon: '∞',
            isUrgent: false,
            daysRemaining: null
        };
    }

    const end = new Date(endDateValue);
    const now = new Date();
    const daysRemaining = daysBetween(now, end);

    if (daysRemaining < 0) {
        return {
            hasEndDate: true,
            display: 'Ended',
            icon: '✓',
            isUrgent: false,
            daysRemaining: 0,
            isEnded: true
        };
    }

    if (daysRemaining === 0) {
        return {
            hasEndDate: true,
            display: 'Ends today',
            icon: '⚠️',
            isUrgent: true,
            daysRemaining: 0
        };
    }

    if (daysRemaining <= 3) {
        return {
            hasEndDate: true,
            display: `Ends in ${daysRemaining} day${daysRemaining === 1 ? '' : 's'}`,
            icon: '⚠️',
            isUrgent: true,
            daysRemaining
        };
    }

    return {
        hasEndDate: true,
        display: `Ends in ${daysRemaining} days`,
        icon: '📅',
        isUrgent: false,
        daysRemaining
    };
};

// ===== Insight Generation =====

/**
 * Insight types
 */
export const INSIGHT_TYPES = {
    BUDGET_WASTE: 'budget_waste',
    SCALE_OPPORTUNITY: 'scale_opportunity',
    TREND_ALERT: 'trend_alert',
    CREATIVE_FATIGUE: 'creative_fatigue',
    PLACEMENT_OPTIMIZATION: 'placement_optimization',
    PERFORMING_WELL: 'performing_well',
    AUDIENCE_EXPANSION: 'audience_expansion',
    BUDGET_EXHAUSTION: 'budget_exhaustion',
    ENDING_SOON: 'ending_soon'
};

/**
 * Generate objective-aware insight for a campaign
 * @param {Object} campaign - Campaign object
 * @param {Object} previousMetrics - Previous period metrics for comparison
 * @returns {Object|null} - Insight object or null
 */
export const generateCampaignInsight = (campaign, previousMetrics = null) => {
    const objective = normalizeObjective(campaign.objective);
    const config = OBJECTIVE_CONFIG[objective];
    const metrics = campaign.currentMetrics || campaign.insights?.data?.[0] || {};
    const health = evaluateCampaignHealth(campaign);
    const pacing = calculateBudgetPacing(campaign);
    const endStatus = getCampaignEndStatus(campaign);

    const spend = extractMetricValue(metrics, 'spend') || 0;
    const primaryValue = extractMetricValue(metrics, config.primaryKPI);
    const impressions = extractMetricValue(metrics, 'impressions') || 0;
    const clicks = extractMetricValue(metrics, 'clicks') || 0;
    const conversions = extractMetricValue(metrics, 'conversions') || 0;

    // Check for ending soon
    if (endStatus.isUrgent && !endStatus.isEnded) {
        const performanceContext = health.status === HEALTH_STATUS.EXCELLENT || health.status === HEALTH_STATUS.GOOD
            ? `The campaign is performing well with ${health.formattedValue}, so extending could continue to drive results.`
            : `Current performance shows ${health.formattedValue}. Review whether the campaign is meeting objectives before extending.`;

        return {
            id: `${campaign.id}-ending-soon`,
            type: INSIGHT_TYPES.ENDING_SOON,
            priority: 'high',
            title: `Campaign ending soon: ${campaign.name}`,
            description: `This campaign ${endStatus.display.toLowerCase()} and has spent ${formatCurrency(spend)} so far. ${performanceContext}`,
            reasoning: `Campaigns ending soon need review to avoid missed opportunities. With ${endStatus.daysRemaining || 0} days remaining, you should decide whether to extend, pause, or let it complete.`,
            expectedOutcome: health.status === HEALTH_STATUS.EXCELLENT || health.status === HEALTH_STATUS.GOOD
                ? `If extended: The campaign will continue generating results at the current efficiency level. If not extended: Campaign will stop and you may lose momentum with your audience.`
                : `Review the performance data to decide. If performance is poor, letting it end saves budget. If acceptable, consider optimizing before extending.`,
            campaignId: campaign.id,
            platform: campaign.platform,
            objective: config.displayLabel,
            action: 'Review',
            metrics: { spend, impressions, clicks, conversions, primaryKPI: health.formattedValue }
        };
    }

    // Check for budget exhaustion
    if (pacing.status === PACING_STATUS.OVERPACING && pacing.percentage > 90) {
        const budgetRemaining = pacing.remaining || 0;
        return {
            id: `${campaign.id}-budget-exhaustion`,
            type: INSIGHT_TYPES.BUDGET_EXHAUSTION,
            priority: 'critical',
            title: `Budget nearly exhausted: ${campaign.name}`,
            description: `This campaign has used ${pacing.percentage.toFixed(0)}% of its budget (${formatCurrency(spend)} of ${formatCurrency(pacing.budget || spend)}), with only ${formatCurrency(budgetRemaining)} remaining. Current performance: ${health.formattedValue}.`,
            reasoning: `The campaign is spending faster than expected. ${health.status === HEALTH_STATUS.EXCELLENT || health.status === HEALTH_STATUS.GOOD
                ? 'Since performance is strong, this indicates high demand for your ads - a good sign that scaling could be effective.'
                : 'Since performance is below targets, the rapid spend may be wasting budget without proportional returns.'}`,
            expectedOutcome: health.status === HEALTH_STATUS.EXCELLENT || health.status === HEALTH_STATUS.GOOD
                ? `Increasing budget will allow the campaign to continue reaching your audience and generating results. Without more budget, the campaign will pause and lose momentum.`
                : `Consider pausing to investigate why spend is high but results are low. Check targeting, creative fatigue, or competition before adding more budget.`,
            campaignId: campaign.id,
            platform: campaign.platform,
            objective: config.displayLabel,
            action: health.status === HEALTH_STATUS.EXCELLENT || health.status === HEALTH_STATUS.GOOD ? 'Increase Budget' : 'Review',
            metrics: { spend, budget: pacing.budget, remaining: budgetRemaining, primaryKPI: health.formattedValue }
        };
    }

    // Check critical health (objective-aware)
    if (health.status === HEALTH_STATUS.CRITICAL && spend > 50) {
        const wastedEstimate = spend * 0.5; // Estimate 50% of spend is inefficient
        return {
            id: `${campaign.id}-budget-waste`,
            type: INSIGHT_TYPES.BUDGET_WASTE,
            priority: 'critical',
            title: `Underperforming: ${campaign.name}`,
            description: `This ${config.displayLabel.toLowerCase()} campaign has spent ${formatCurrency(spend)} but is significantly underperforming with ${health.formattedValue}. The target for this objective type is ${config.lowerIsBetter ? 'below' : 'above'} ${config.lowerIsBetter ? formatCurrency(config.successThreshold) : config.successThreshold + 'x'}.`,
            reasoning: `With ${health.formattedValue}, this campaign is ${config.lowerIsBetter ? 'spending too much' : 'not generating enough return'} per result. Every day it runs, budget is being used inefficiently. Common causes include: poor audience targeting, creative fatigue, high competition, or misaligned landing pages.`,
            expectedOutcome: `Pausing: Immediately stops further inefficient spend, preserving budget for better-performing campaigns. Optimizing: Review targeting, creatives, and placements - improvements could turn this into a profitable campaign. Continuing as-is: Will likely continue underperforming and waste additional budget.`,
            campaignId: campaign.id,
            platform: campaign.platform,
            objective: config.displayLabel,
            action: 'Pause',
            impactAmount: wastedEstimate,
            impactType: 'save',
            metrics: { spend, impressions, clicks, conversions, primaryKPI: health.formattedValue }
        };
    }

    // Check for scale opportunity (objective-aware)
    if (health.status === HEALTH_STATUS.EXCELLENT && spend > 100) {
        const scalePotential = spend * 0.5; // Estimate 50% budget increase potential
        return {
            id: `${campaign.id}-scale-opportunity`,
            type: INSIGHT_TYPES.SCALE_OPPORTUNITY,
            priority: 'medium',
            title: `Scale opportunity: ${campaign.name}`,
            description: `This campaign is exceeding targets with ${health.formattedValue} on ${formatCurrency(spend)} spend. It has generated ${impressions.toLocaleString()} impressions, ${clicks.toLocaleString()} clicks, and ${conversions} conversions - indicating strong audience engagement and efficient ad delivery.`,
            reasoning: `When a campaign consistently outperforms benchmarks, it typically means the targeting, creative, and offer are resonating well with the audience. Increasing budget allows the algorithm to find more similar users, potentially scaling results proportionally. The ${health.formattedValue} performance suggests there's headroom to spend more while maintaining efficiency.`,
            expectedOutcome: `Increasing budget by 20-50% should allow the campaign to reach more of your target audience while maintaining similar efficiency. Monitor closely for the first 2-3 days after scaling - performance may temporarily dip as the algorithm adjusts, but should stabilize. If efficiency drops significantly, you can always scale back down.`,
            campaignId: campaign.id,
            platform: campaign.platform,
            objective: config.displayLabel,
            action: 'Scale',
            impactAmount: scalePotential,
            impactType: 'gain',
            metrics: { spend, impressions, clicks, conversions, primaryKPI: health.formattedValue }
        };
    }

    // Check for good performance (not excellent, but solid)
    if (health.status === HEALTH_STATUS.GOOD && spend > 100) {
        return {
            id: `${campaign.id}-performing-well`,
            type: INSIGHT_TYPES.PERFORMING_WELL,
            priority: 'low',
            title: `On track: ${campaign.name}`,
            description: `This campaign is meeting targets with ${health.formattedValue} on ${formatCurrency(spend)} spend. Performance is solid with ${impressions.toLocaleString()} impressions and ${clicks.toLocaleString()} clicks.`,
            reasoning: `The campaign is performing within acceptable ranges for a ${config.displayLabel.toLowerCase()} objective. While not exceptional, it's generating positive returns and contributing to your marketing goals.`,
            expectedOutcome: `Continue monitoring. Small optimizations to creative or targeting could push performance from "good" to "excellent." Consider A/B testing new ad variations or expanding to similar audiences.`,
            campaignId: campaign.id,
            platform: campaign.platform,
            objective: config.displayLabel,
            action: 'Monitor',
            metrics: { spend, impressions, clicks, conversions, primaryKPI: health.formattedValue }
        };
    }

    // Check trend if previous data available
    if (previousMetrics && primaryValue !== null) {
        const previousValue = extractMetricValue(previousMetrics, config.primaryKPI);
        if (previousValue !== null) {
            const trend = calculateObjectiveAwareTrend(primaryValue, previousValue, objective, config.primaryKPI);

            // Significant negative trend
            if (!trend.isPositive && trend.percentage > 20) {
                const trendDirection = config.lowerIsBetter ? 'increased' : 'decreased';
                return {
                    id: `${campaign.id}-trend-alert`,
                    type: INSIGHT_TYPES.TREND_ALERT,
                    priority: 'high',
                    title: `Performance declining: ${campaign.name}`,
                    description: `This campaign's ${config.primaryKPI.toUpperCase()} has ${trendDirection} by ${trend.percentage.toFixed(1)}% compared to the previous period (was ${config.formatPrimary(previousValue)}, now ${health.formattedValue}). Total spend this period: ${formatCurrency(spend)}.`,
                    reasoning: `A ${trend.percentage.toFixed(0)}%+ decline in primary metrics often indicates: creative fatigue (audience has seen ads too many times), increased competition (more advertisers targeting same audience), seasonal changes in user behavior, or changes in your landing page/offer. The sooner you identify the cause, the faster you can correct course.`,
                    expectedOutcome: `Investigating now can prevent further decline. Check: creative frequency (>3 may indicate fatigue), auction insights for competition changes, and recent changes to landing pages. Refreshing creatives or adjusting bids often reverses declining trends within 3-5 days.`,
                    campaignId: campaign.id,
                    platform: campaign.platform,
                    objective: config.displayLabel,
                    action: 'Analyze',
                    metrics: {
                        spend,
                        impressions,
                        clicks,
                        conversions,
                        primaryKPI: health.formattedValue,
                        previousKPI: config.formatPrimary(previousValue),
                        trendPercentage: trend.percentage
                    }
                };
            }
        }
    }

    // No significant insight
    return null;
};

/**
 * Generate multiple insights for a list of campaigns
 * @param {Array} campaigns - List of campaigns
 * @param {Object} previousPeriodData - Previous period data keyed by campaign ID
 * @returns {Array} - Array of insights sorted by priority
 */
export const generateInsights = (campaigns, previousPeriodData = {}) => {
    const insights = [];

    for (const campaign of campaigns) {
        const previousMetrics = previousPeriodData[campaign.id];
        const insight = generateCampaignInsight(campaign, previousMetrics);
        if (insight) {
            insights.push(insight);
        }
    }

    // Sort by priority
    const priorityOrder = { critical: 0, high: 1, medium: 2, low: 3 };
    return insights.sort((a, b) =>
        (priorityOrder[a.priority] || 3) - (priorityOrder[b.priority] || 3)
    );
};

// ===== Utility Functions =====

/**
 * Calculate days between two dates
 * @param {Date|string} start - Start date
 * @param {Date|string} end - End date
 * @returns {number} - Number of days
 */
export const daysBetween = (start, end) => {
    const startDate = new Date(start);
    const endDate = new Date(end);
    const diffTime = endDate - startDate;
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

/**
 * Format currency value
 * @param {number} amount - Amount to format
 * @param {string} currency - Currency code
 * @returns {string} - Formatted currency string
 */
export const formatCurrency = (amount, currency = 'USD') => {
    if (amount >= 1000000) {
        return `$${(amount / 1000000).toFixed(1)}M`;
    }
    if (amount >= 1000) {
        return `$${(amount / 1000).toFixed(1)}K`;
    }
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
    }).format(amount);
};

/**
 * Get status color for health status
 * @param {string} status - Health status
 * @returns {string} - CSS color variable
 */
export const getHealthStatusColor = (status) => {
    const colors = {
        [HEALTH_STATUS.EXCELLENT]: 'var(--color-success)',
        [HEALTH_STATUS.GOOD]: 'var(--color-success)',
        [HEALTH_STATUS.WARNING]: 'var(--color-warning)',
        [HEALTH_STATUS.CRITICAL]: 'var(--color-error)',
        [HEALTH_STATUS.UNKNOWN]: 'var(--color-text-secondary)'
    };
    return colors[status] || colors[HEALTH_STATUS.UNKNOWN];
};

/**
 * Get status color for pacing status
 * @param {string} status - Pacing status
 * @returns {string} - CSS color variable
 */
export const getPacingStatusColor = (status) => {
    const colors = {
        [PACING_STATUS.ON_TRACK]: 'var(--color-success)',
        [PACING_STATUS.UNDERPACING]: 'var(--color-warning)',
        [PACING_STATUS.OVERPACING]: 'var(--color-error)',
        [PACING_STATUS.COMPLETED]: 'var(--color-text-secondary)',
        [PACING_STATUS.UNKNOWN]: 'var(--color-text-secondary)'
    };
    return colors[status] || colors[PACING_STATUS.UNKNOWN];
};

export default {
    // Objectives
    CAMPAIGN_OBJECTIVES,
    OBJECTIVE_CONFIG,
    normalizeObjective,
    getPrimaryKPI,
    getSecondaryKPIs,
    getObjectiveConfig,

    // Health
    HEALTH_STATUS,
    evaluateCampaignHealth,
    extractMetricValue,

    // Display
    formatCampaignMetric,
    getCampaignDisplayData,

    // Trends
    calculateTrend,
    calculateObjectiveAwareTrend,

    // Pacing
    PACING_STATUS,
    calculateBudgetPacing,

    // End dates
    getCampaignEndStatus,

    // Insights
    INSIGHT_TYPES,
    generateCampaignInsight,
    generateInsights,

    // Utilities
    daysBetween,
    formatCurrency,
    getHealthStatusColor,
    getPacingStatusColor
};
